import { useState } from "react"
import { useParams, Link, useNavigate } from "react-router-dom"
import { useQuery, useMutation } from "urql"
import { RefreshCw, Eraser, Pencil, RotateCcw } from "lucide-react"
import { toast } from "sonner"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"

import { StatusBadge } from "@/components/shared/status-badge"
import { TimeAgo } from "@/components/shared/time-ago"
import { CopyButton } from "@/components/shared/copy-button"
import { JsonViewer } from "@/components/shared/json-viewer"
import { DataTable, type Column } from "@/components/shared/data-table"
import { EmptyState } from "@/components/shared/empty-state"
import { GlassCard } from "@/components/shared/glass-card"
import { FieldLabel, sectionTitleClass } from "@/components/shared/field-label"
import { ConfirmDialog } from "@/components/shared/confirm-dialog"
import { ShareLinkDialog } from "@/components/shared/share-link-dialog"
import { SourceForm } from "../components/source-form"
import { SourceQuery, EventsQuery } from "@/lib/graphql/queries"
import {
  RotateSourceSecretMutation,
  ClearSourceSecondarySecretMutation,
  ReplayBySourceMutation,
} from "@/lib/graphql/mutations"
import type { ResultOf } from "@/lib/graphql/types"

type SourceSub = NonNullable<ResultOf<typeof SourceQuery>["source"]>["subscriptions"][number]
type EventItem = ResultOf<typeof EventsQuery>["events"]["nodes"][number]

const subColumns: Column<SourceSub>[] = [
  {
    header: "Name",
    cell: (row) => <span className="font-medium">{row.name}</span>,
  },
  {
    header: "Destination",
    cell: (row) => (
      <Link to={`/destinations/${row.destination.id}`} className="text-xs hover:underline">
        {row.destination.name}
      </Link>
    ),
  },
  {
    header: "Filter",
    cell: (row) => (
      <span className="text-xs text-muted-foreground font-mono">
        {row.filterConfig?.eventTypes?.join(", ") || "*"}
      </span>
    ),
  },
  {
    header: "Status",
    cell: (row) => (
      <Badge variant={row.enabled ? "default" : "secondary"} className="text-2xs">
        {row.enabled ? "Enabled" : "Disabled"}
      </Badge>
    ),
  },
]

const eventColumns: Column<EventItem>[] = [
  {
    header: "Event Type",
    cell: (row) => <code className="text-xs font-mono">{row.eventType}</code>,
  },
  {
    header: "Status",
    cell: (row) => <StatusBadge status={row.status} />,
  },
  {
    header: "Received",
    cell: (row) => <TimeAgo date={row.receivedAt} />,
  },
]

export function SourceDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [result, reexecute] = useQuery({ query: SourceQuery, variables: { id: id! } })
  const [, rotateSecret] = useMutation(RotateSourceSecretMutation)
  const [, clearSecondary] = useMutation(ClearSourceSecondarySecretMutation)
  const [, replayBySource] = useMutation(ReplayBySourceMutation)
  const [editOpen, setEditOpen] = useState(false)
  const [rotateOpen, setRotateOpen] = useState(false)
  const [rotating, setRotating] = useState(false)
  const [clearOpen, setClearOpen] = useState(false)
  const [clearing, setClearing] = useState(false)
  const [replayOpen, setReplayOpen] = useState(false)
  const [replaySince, setReplaySince] = useState("1h")
  const [replaying, setReplaying] = useState(false)
  const [events] = useQuery({
    query: EventsQuery,
    variables: { sourceId: id, limit: 10, offset: 0 },
  })

  const source = result.data?.source

  if (result.fetching) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-40 w-full" />
      </div>
    )
  }

  if (!source) {
    return <EmptyState title="Source not found" />
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between pb-6">
        <div className="flex items-center gap-3">
          <h1 className="text-3xl font-bold tracking-tight">{source.name}</h1>
          <StatusBadge status={source.status} />
        </div>
        <div className="flex items-center gap-2">
          <ShareLinkDialog scopeType="source" scopeId={id!} />
          <Button variant="outline" size="sm" onClick={() => setReplayOpen(true)}>
            <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
            Replay Events
          </Button>
          <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
            <Pencil className="h-3.5 w-3.5 mr-1.5" />
            Edit
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 border-border/30">
          <CardHeader>
            <CardTitle className={sectionTitleClass}>Configuration</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <FieldLabel className="mb-1">Slug</FieldLabel>
                <div className="flex items-center gap-1">
                  <code className="font-mono text-sm">{source.slug}</code>
                  <CopyButton text={source.slug} />
                </div>
              </div>
              <div>
                <FieldLabel className="mb-1">Provider</FieldLabel>
                <div className="capitalize">{source.providerType}</div>
              </div>
              <div>
                <FieldLabel className="mb-1">Rate Limit</FieldLabel>
                <div>{source.rateLimitRps > 0 ? `${source.rateLimitRps} rps` : "Unlimited"}</div>
              </div>
              <div>
                <FieldLabel className="mb-1">Spike Protection</FieldLabel>
                <div className="flex items-center gap-2">
                  <Badge variant={source.spikeProtection ? "default" : "secondary"} className="text-2xs">
                    {source.spikeProtection ? "Enabled" : "Disabled"}
                  </Badge>
                  {source.spikeProtection && source.maxIngestRps > 0 && (
                    <span className="text-xs text-muted-foreground tabular-nums">
                      {source.maxIngestRps} rps threshold
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div>
              <FieldLabel className="mb-2">Ingest URL</FieldLabel>
              <GlassCard className="p-3 flex items-center gap-2">
                <code className="font-mono text-sm flex-1 truncate">/ingest/{source.slug}</code>
                <CopyButton text={`/ingest/${source.slug}`} />
              </GlassCard>
            </div>
            {source.verifyConfig && (
              <div>
                <FieldLabel className="mb-2">Verify Config</FieldLabel>
                <JsonViewer data={source.verifyConfig} collapsed />
              </div>
            )}
            {source.brokerConfig && (
              <div>
                <FieldLabel className="mb-2">Broker Config</FieldLabel>
                <JsonViewer data={source.brokerConfig} collapsed />
              </div>
            )}
            {source.responseConfig && (
              <div>
                <FieldLabel className="mb-2">Custom Response</FieldLabel>
                <GlassCard className="p-3">
                  <div className="grid grid-cols-3 gap-3 text-xs">
                    <div>
                      <div className="text-muted-foreground">Status Code</div>
                      <div className="font-medium tabular-nums">{source.responseConfig.statusCode}</div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">Content Type</div>
                      <div className="font-medium font-mono">{source.responseConfig.contentType}</div>
                    </div>
                    {source.responseConfig.body && (
                      <div>
                        <div className="text-muted-foreground">Body</div>
                        <div className="font-medium font-mono truncate">{source.responseConfig.body}</div>
                      </div>
                    )}
                  </div>
                </GlassCard>
              </div>
            )}
            {source.dedupConfig && (
              <div>
                <FieldLabel className="mb-2">Deduplication</FieldLabel>
                <GlassCard className="p-3">
                  <div className="grid grid-cols-3 gap-3 text-xs">
                    <div>
                      <div className="text-muted-foreground">Strategy</div>
                      <div className="font-medium capitalize">{source.dedupConfig.strategy}</div>
                    </div>
                    {source.dedupConfig.window && (
                      <div>
                        <div className="text-muted-foreground">Window</div>
                        <div className="font-medium font-mono">{source.dedupConfig.window}</div>
                      </div>
                    )}
                    {source.dedupConfig.fields && source.dedupConfig.fields.length > 0 && (
                      <div>
                        <div className="text-muted-foreground">Fields</div>
                        <div className="font-medium font-mono">{source.dedupConfig.fields.join(", ")}</div>
                      </div>
                    )}
                  </div>
                </GlassCard>
              </div>
            )}
            {source.verifyConfig && source.providerType !== "none" && (
              <div>
                <FieldLabel className="mb-2">Secret Rotation</FieldLabel>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setRotateOpen(true)}
                  >
                    <RefreshCw className="h-3 w-3 mr-1.5" />
                    Rotate Secret
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setClearOpen(true)}
                  >
                    <Eraser className="h-3 w-3 mr-1.5" />
                    Clear Secondary
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-border/30">
          <CardHeader>
            <CardTitle className={sectionTitleClass}>Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div>
              <FieldLabel className="mb-1">ID</FieldLabel>
              <div className="flex items-center gap-1">
                <code className="font-mono text-xs">{source.id}</code>
                <CopyButton text={source.id} />
              </div>
            </div>
            <div>
              <FieldLabel className="mb-1">Created</FieldLabel>
              <TimeAgo date={source.createdAt} />
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="space-y-4">
        <h2 className="text-sm font-medium">Subscriptions</h2>
        <DataTable
          columns={subColumns}
          data={source.subscriptions}
          keyFn={(row) => row.id}
          emptyTitle="No subscriptions"
          emptyDescription="Link this source to a destination to start routing events"
        />
      </div>

      <div className="space-y-4">
        <h2 className="text-sm font-medium">Recent Events</h2>
        <DataTable
          columns={eventColumns}
          data={events.data?.events.nodes ?? []}
          keyFn={(row) => row.id}
          loading={events.fetching}
          emptyTitle="No events"
          onRowClick={(row) => navigate(`/events/${row.id}`)}
        />
      </div>

      <SourceForm
        open={editOpen}
        onOpenChange={setEditOpen}
        onSuccess={() => reexecute({ requestPolicy: "network-only" })}
        initialData={source}
      />

      <ConfirmDialog
        open={rotateOpen}
        onOpenChange={setRotateOpen}
        title="Rotate Source Secret"
        description="The current secret will become the secondary secret for key rotation. A new primary secret will be generated. Recipients should verify against both secrets during the transition."
        confirmLabel="Rotate"
        onConfirm={async () => {
          setRotating(true)
          const res = await rotateSecret({ id: id! })
          setRotating(false)
          if (res.error) { toast.error(res.error.message); return }
          toast.success("Source secret rotated")
          setRotateOpen(false)
          reexecute({ requestPolicy: "network-only" })
        }}
        loading={rotating}
      />

      <ConfirmDialog
        open={clearOpen}
        onOpenChange={setClearOpen}
        title="Clear Secondary Secret"
        description="This will remove the secondary (old) secret. Make sure all consumers have switched to the new primary secret before clearing."
        confirmLabel="Clear"
        onConfirm={async () => {
          setClearing(true)
          const res = await clearSecondary({ id: id! })
          setClearing(false)
          if (res.error) { toast.error(res.error.message); return }
          toast.success("Secondary secret cleared")
          setClearOpen(false)
          reexecute({ requestPolicy: "network-only" })
        }}
        loading={clearing}
        destructive
      />

      <Dialog open={replayOpen} onOpenChange={setReplayOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Replay Events by Source</DialogTitle>
          </DialogHeader>
          <form
            onSubmit={async (e) => {
              e.preventDefault()
              setReplaying(true)
              const res = await replayBySource({ sourceId: id!, since: replaySince })
              setReplaying(false)
              if (res.error) { toast.error(res.error.message); return }
              toast.success(`Replayed: ${res.data?.replayBySource.deliveries ?? 0} deliveries created`)
              setReplayOpen(false)
              reexecute({ requestPolicy: "network-only" })
            }}
            className="space-y-4"
          >
            <div className="space-y-2">
              <Label htmlFor="since">Replay events from the last</Label>
              <Input
                id="since"
                value={replaySince}
                onChange={(e) => setReplaySince(e.target.value)}
                placeholder="1h"
              />
              <p className="text-xs text-muted-foreground">
                Duration format: 1h, 30m, 24h, 168h (7 days)
              </p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setReplayOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={replaying || !replaySince}>
                {replaying ? "Replaying..." : "Replay"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  )
}
